import { inject, injectable, injectFromBase } from 'inversify'
import type { Router } from 'vue-router'
import ExternalTrackerService from '@/services/tracking/externalTrackerService'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { Plugin } from '@/composition/vue/compositionApi'
import type { ExternalTrackerAsPlugin, TrackableConfig } from '@/services/tracking/types'
import { ROUTER_AFTER_EACH } from '@/services/route/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type RouterService from '@/services/route/routerService'
import type SubscriptionService from '@/services/transport/subscriptionService'
import type UserService from '@/services/domain/user/userService'

@injectable()
@injectFromBase()
export class MetaPixelTrackingService extends ExternalTrackerService {
  /**
   * URL query parameter keys that Meta has flagged as containing PII (Personally Identifiable Information).
   * These are stripped from the `dl` (document location) field in pixel requests.
   * Original list is from https://textmagic.atlassian.net/browse/CAR-16189
   */
  protected readonly BLOCKED_PII_PARAMS = new Set(['user', 'name', 'firstName', 'lastName', 'text', 's'])

  protected isTransportPatched = false

  constructor(
    @inject(SERVICE_TYPES.RouterService) protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.SubscriptionService) protected readonly subscriptionService: SubscriptionService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    super()
  }

  private trackingId = '652787705180119'

  private userId: string | undefined = undefined

  public getConfig(): TrackableConfig {
    return {
      id: this.trackingId,
    }
  }

  public getPlugin(): Plugin {
    return {
      install: () => this.initialize(),
    } satisfies Plugin
  }

  /**
   * Intercepts Image constructor and sendBeacon so that any request to Meta Pixel
   * has URL query parameters stripped from the `dl` field, preventing PII leaks.
   */
  private patchTransportsForPiiFiltering(): void {
    if (this.isTransportPatched) return
    this.isTransportPatched = true

    const cleanUrl = (url: string) => this.stripBlockedParamsFromDl(url)
    const OriginalImage = window.Image
    // Safe: 'src' property always exists on HTMLImageElement.prototype
    const srcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src')!

    window.Image = function (...args: ConstructorParameters<typeof OriginalImage>) {
      const img = new OriginalImage(...args)
      Object.defineProperty(img, 'src', {
        set(value: string) {
          // Safe: srcDescriptor is from HTMLImageElement.prototype which always has getter/setter
          srcDescriptor.set!.call(this, cleanUrl(value))
        },
        get() {
          // Safe: srcDescriptor is from HTMLImageElement.prototype which always has getter/setter
          return srcDescriptor.get!.call(this)
        },
        configurable: true,
      })
      return img
    } as unknown as typeof Image
    window.Image.prototype = OriginalImage.prototype

    if (navigator.sendBeacon) {
      const originalSendBeacon = navigator.sendBeacon.bind(navigator)
      navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
        return originalSendBeacon(typeof url === 'string' ? cleanUrl(url) : url, data)
      }
    }
  }

  protected async initialize(): Promise<void> {
    const trackingId = this.trackingId
    const subscriptionKey = 'metaPixelAnalytics'
    const router: Router = this.routerService.getRouter()
    /* eslint-disable */
    const prepareTracker = () => {
      return new Promise<void>((resolve) => {
        let isSettled = false
        const finish = () => {
          if (isSettled) return
          isSettled = true
          resolve()
        }

        !(function (f, b, e, v, n, t, s) {
          // @ts-expect-error tracker initial script from docs
          if (f.fbq) return void finish()
          // @ts-expect-error tracker initial script from docs
          n = f.fbq = function () {
            // @ts-expect-error tracker initial script from docs
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
          }
          // @ts-expect-error tracker initial script from docs
          if (!f._fbq) f._fbq = n
          // @ts-expect-error tracker initial script from docs
          n.push = n
          // @ts-expect-error tracker initial script from docs
          n.loaded = !0
          // @ts-expect-error tracker initial script from docs
          n.version = '2.0'
          // @ts-expect-error tracker initial script from docs
          n.queue = []
          // @ts-expect-error tracker initial script from docs
          t = b.createElement(e)
          // @ts-expect-error tracker initial script from docs
          t.async = !0
          // @ts-expect-error tracker initial script from docs
          t.src = v
          // @ts-expect-error tracker initial script from docs
          t.onload = finish
          // @ts-expect-error tracker initial script from docs
          t.onerror = finish

          // waiting for 10s for reaction, if nothing happens resolve promise
          setTimeout(() => {
            finish()
          }, 10_000)

          // @ts-expect-error tracker initial script from docs
          s = b.getElementsByTagName(e)[0]
          // @ts-expect-error tracker initial script from docs
          s.parentNode.insertBefore(t, s)
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
      })
    }
    const init = (userId: string) => {
      // @ts-expect-error tracker initial script from docs
      fbq('set', 'autoConfig', false, trackingId)
      // @ts-expect-error tracker initial script from docs
      fbq('init', trackingId, { external_id: [userId] })
    }
    const track = () => {
      const currentUserId = this.userService.getCurrentUserOrNull()?.id
      if (currentUserId) {
        if (this.userId !== currentUserId) {
          init(currentUserId)
          this.userId = currentUserId
        }
        // @ts-expect-error tracker initial script from docs
        fbq('track', 'PageView')
      }
    }
    const trackingHandler = async () => {
      this.patchTransportsForPiiFiltering()
      await prepareTracker()
      await router.isReady()
      track() // first track action. Other track will fire on route change
      if (!this.subscriptionService.exist(subscriptionKey)) {
        this.subscriptionService.subscribeWithSubscriptionKey(ROUTER_AFTER_EACH, track, subscriptionKey, true)
      }
    }
    /* eslint-enable */
    const trackerAsPlugin: ExternalTrackerAsPlugin = {
      trackingHandler,
      loadOnCustomEvent: true,
    }
    this.initializeTrackerAsPlugin(trackerAsPlugin)
  }

  /**
   * Strips blocked query parameters from the `dl` param in Meta Pixel requests.
   * Meta blocks events containing PII (names, emails, etc.) leaked via URL query params.
   * Since the pixel SDK provides no API to override the captured URL, we intercept
   * outgoing requests and clean the `dl` parameter before they reach Meta servers.
   */
  public stripBlockedParamsFromDl(url: string): string {
    try {
      const pixelUrl = new URL(url)

      const isMetaPixelRequest =
        (pixelUrl.hostname === 'facebook.com' || pixelUrl.hostname === 'www.facebook.com') &&
        pixelUrl.pathname.startsWith('/tr')
      if (!isMetaPixelRequest) {
        return url
      }

      const dl = pixelUrl.searchParams.get('dl')
      if (!dl) return pixelUrl.toString()

      pixelUrl.searchParams.set('dl', this.stripBlockedParamsFromUrl(dl))
      return pixelUrl.toString()
    } catch {
      return url
    }
  }

  protected stripBlockedParamsFromUrl(rawUrl: string): string {
    try {
      const pageUrl = new URL(rawUrl)

      for (const key of Array.from(pageUrl.searchParams.keys())) {
        const rootKey = key.split('[', 1)[0]
        if (this.BLOCKED_PII_PARAMS.has(rootKey)) pageUrl.searchParams.delete(key)
      }

      return pageUrl.toString()
    } catch {
      return rawUrl
    }
  }
}
